/** @format */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  ExternalLink,
  MoreVertical,
  Search,
  SlidersHorizontal,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Header from "../../components/ui/Header";
import HeroSection from "../../components/ui/HeroSection";
import { Toast } from "../../components/ui/Toast";
import { useToast } from "../../hooks/useToast";
import { useFeedbackStore } from "../../store/useFeedbackStore";
import {
  formatFeedbackCategoryLabel,
  type FeedbackStatus,
} from "../../api/feedbackService";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { useOrganizationStore } from "../../store/useOrganizationStore";
import { useSopStore } from "../../store/useSopStore";
import { useOrgRole } from "../../hooks/useOrgRole";
import "../../components/ui/Header.css";

const CATEGORY_LABELS: Record<string, string> = {
  unclear: "Unclear",
  incorrect: "Incorrect",
  missing_step: "Missing Step",
  other: "Other",
};

const STATUS_BADGE: Record<string, string> = {
  open: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  resolved: "bg-green-500/20 text-green-300 border-green-500/30",
  stale: "bg-violet-500/15 text-violet-200 border-violet-500/30",
};

const PAGE_SIZES = [20, 50, 100] as const;

function clampPageSize(value: number) {
  return (PAGE_SIZES as readonly number[]).includes(value) ? value : 20;
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const FeedbackPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    allFeedback,
    pagination,
    loading,
    fetchAllFeedback,
    updateFeedbackStatus,
    deleteFeedback,
  } = useFeedbackStore();
  const { organizations } = useOrganizationStore();
  const { sops, fetchSOPs } = useSopStore();
  const { toast, showToast, hideToast } = useToast();
  const { canManageFeedback } = useOrgRole();
  const list = allFeedback ?? [];

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const limit = clampPageSize(
    parseInt(searchParams.get("limit") ?? "20", 10) || 20,
  );

  const status = (searchParams.get("status") ?? "all").trim();
  const category = (searchParams.get("category") ?? "").trim();
  const sopId = (searchParams.get("sopId") ?? "").trim();
  const organizationId = (searchParams.get("organizationId") ?? "").trim();
  const versionNumParam = (searchParams.get("versionNum") ?? "").trim();
  const versionNum = versionNumParam
    ? parseInt(versionNumParam, 10)
    : undefined;

  const sopNameParam = (searchParams.get("sopName") ?? "").trim();
  const ownerParam = (searchParams.get("owner") ?? "").trim();

  const [sopNameInput, setSopNameInput] = useState(sopNameParam);
  const [ownerInput, setOwnerInput] = useState(ownerParam);

  const [confirmDelete, setConfirmDelete] = useState<{
    sopId: string;
    feedbackId: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [openMenuForId, setOpenMenuForId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement | null>(null);

  const setParams = useCallback(
    (
      patch: Record<string, string | number | null | undefined>,
      opts?: { resetPage?: boolean },
    ) => {
      const next = new URLSearchParams(searchParams);
      for (const [k, v] of Object.entries(patch)) {
        if (
          v === null ||
          v === undefined ||
          String(v).trim() === "" ||
          v === "all"
        ) {
          next.delete(k);
        } else {
          next.set(k, String(v));
        }
      }
      if (opts?.resetPage) next.set("page", "1");
      if (!next.get("limit")) next.set("limit", String(limit));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, limit],
  );

  useEffect(() => {
    setSopNameInput(sopNameParam);
  }, [sopNameParam]);

  useEffect(() => {
    setOwnerInput(ownerParam);
  }, [ownerParam]);

  useEffect(() => {
    fetchAllFeedback(undefined, {
      status: status === "all" ? undefined : (status as FeedbackStatus),
      category: category || undefined,
      sopId: sopId || undefined,
      organizationId: organizationId || undefined,
      versionNum:
        typeof versionNum === "number" && !Number.isNaN(versionNum)
          ? versionNum
          : undefined,
      sopName: sopNameParam || undefined,
      owner: ownerParam || undefined,
      page,
      limit,
    });
  }, [
    fetchAllFeedback,
    status,
    category,
    sopId,
    organizationId,
    versionNum,
    sopNameParam,
    ownerParam,
    page,
    limit,
  ]);

  useEffect(() => {
    // Populate SOP filter dropdown (best-effort, small list).
    if (!sops || sops.length === 0) {
      fetchSOPs({ page: 1, limit: 100 });
    }
  }, [fetchSOPs, sops]);

  useEffect(() => {
    if (!openMenuForId) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (menuRef.current && target && !menuRef.current.contains(target)) {
        setOpenMenuForId(null);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [openMenuForId]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenMenuForId(null);
        setFiltersOpen(false);
      }
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  useEffect(() => {
    if (!filtersOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (
        filtersRef.current &&
        target &&
        !filtersRef.current.contains(target)
      ) {
        setFiltersOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [filtersOpen]);

  // Debounce text searches; reset to page 1 on change.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (sopNameInput.trim() !== sopNameParam) {
        setParams(
          { sopName: sopNameInput.trim() || null },
          { resetPage: true },
        );
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [sopNameInput, sopNameParam, setParams]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (ownerInput.trim() !== ownerParam) {
        setParams({ owner: ownerInput.trim() || null }, { resetPage: true });
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [ownerInput, ownerParam, setParams]);

  const handleResolve = async (
    sopId: string,
    feedbackId: string,
    current: FeedbackStatus,
  ) => {
    const next: FeedbackStatus = current === "open" ? "resolved" : "open";
    try {
      await updateFeedbackStatus(sopId, feedbackId, next);
      showToast(`Marked as ${next}`, "", "success");
    } catch {
      showToast("Failed to update status", "", "error");
    }
  };

  const handleDelete = async (sopId: string, feedbackId: string) => {
    setConfirmDelete({ sopId, feedbackId });
  };

  const total = pagination?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const sopOptions = useMemo(
    () =>
      (sops ?? [])
        .map((s) => ({ id: s.sopId, title: s.sopTitle || s.sopId }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [sops],
  );

  const ownerLabel = useCallback((f: (typeof list)[number]) => {
    return (
      f.createdByUser?.name ||
      f.createdByUser?.email ||
      f.createdByEmail ||
      (f.createdBy ? f.createdBy.slice(0, 8) + "…" : "—")
    );
  }, []);

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onRemove: () => void }> =
      [];

    if (status && status !== "all") {
      chips.push({
        key: "status",
        label: `Status: ${status}`,
        onRemove: () => setParams({ status: null }, { resetPage: true }),
      });
    }
    if (category) {
      chips.push({
        key: "category",
        label: `Category: ${CATEGORY_LABELS[category] ?? category}`,
        onRemove: () => setParams({ category: null }, { resetPage: true }),
      });
    }
    if (sopId) {
      const sop = sopOptions.find((s) => s.id === sopId);
      chips.push({
        key: "sopId",
        label: `SOP: ${sop?.title ?? sopId.slice(0, 8) + "…"}`,
        onRemove: () => setParams({ sopId: null }, { resetPage: true }),
      });
    }
    if (organizationId) {
      const org = organizations.find((o) => o.orgId === organizationId);
      chips.push({
        key: "organizationId",
        label: `Org: ${org?.name ?? organizationId.slice(0, 8) + "…"}`,
        onRemove: () =>
          setParams({ organizationId: null }, { resetPage: true }),
      });
    }
    if (versionNumParam) {
      chips.push({
        key: "versionNum",
        label: `Version: v${versionNumParam}`,
        onRemove: () => setParams({ versionNum: null }, { resetPage: true }),
      });
    }
    if (sopNameParam) {
      chips.push({
        key: "sopName",
        label: `SOP name: “${sopNameParam}”`,
        onRemove: () => setParams({ sopName: null }, { resetPage: true }),
      });
    }
    if (ownerParam) {
      chips.push({
        key: "owner",
        label: `Owner: “${ownerParam}”`,
        onRemove: () => setParams({ owner: null }, { resetPage: true }),
      });
    }

    return chips;
  }, [
    status,
    category,
    sopId,
    organizationId,
    versionNumParam,
    sopNameParam,
    ownerParam,
    organizations,
    sopOptions,
    setParams,
  ]);

  const clearAll = useCallback(() => {
    setParams(
      {
        status: null,
        category: null,
        sopId: null,
        organizationId: null,
        versionNum: null,
        sopName: null,
        owner: null,
        page: 1,
      },
      { resetPage: true },
    );
    setSopNameInput("");
    setOwnerInput("");
    setFiltersOpen(false);
  }, [setParams]);

  const hasAnyAdvancedFilters = Boolean(
    category || sopId || organizationId || versionNumParam || ownerParam,
  );

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Header />

      <div className="max-w-[1440px] mx-auto px-6 py-8 mt-16">
        <HeroSection
          title="Feedback"
          description="Review and resolve SOP feedback across your workspace."
          className="mb-6"
        />

        {/* Controls */}
        <div className="mb-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                value={sopNameInput}
                onChange={(e) => setSopNameInput(e.target.value)}
                placeholder="Search SOP name…"
                className="w-full rounded-xl border border-slate-700 bg-slate-950/30 pl-9 pr-9 py-2.5 text-sm text-slate-100 outline-none focus:border-cyan-600/80 focus:ring-2 focus:ring-cyan-600/10"
              />
              {sopNameInput.trim() && (
                <button
                  onClick={() => {
                    setSopNameInput("");
                    setParams({ sopName: null }, { resetPage: true });
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-500 hover:text-slate-200 hover:bg-slate-800/60"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {(["all", "open", "resolved"] as const).map((s) => {
                const active = status === s;
                return (
                  <button
                    key={s}
                    onClick={() =>
                      setParams({ status: s }, { resetPage: true })
                    }
                    className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                      active
                        ? "border-cyan-500/40 bg-cyan-500/15 text-cyan-200"
                        : "border-slate-700 bg-slate-950/20 text-slate-200 hover:bg-slate-800/60"
                    }`}
                  >
                    {s === "all" ? "All" : s === "open" ? "Open" : "Resolved"}
                  </button>
                );
              })}

              <div className="relative" ref={filtersRef}>
                <button
                  onClick={() => setFiltersOpen((v) => !v)}
                  className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                    hasAnyAdvancedFilters
                      ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-200"
                      : "border-slate-700 bg-slate-950/20 text-slate-200 hover:bg-slate-800/60"
                  }`}
                  aria-haspopup="dialog"
                  aria-expanded={filtersOpen}
                  title="Advanced filters"
                >
                  <SlidersHorizontal className="w-4 h-4 text-slate-400" />
                  Filters
                  {hasAnyAdvancedFilters && (
                    <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-cyan-500/20 px-1.5 text-xs font-semibold text-cyan-200">
                      {activeFilterChips.length}
                    </span>
                  )}
                </button>

                {filtersOpen && (
                  <div className="absolute right-0 mt-2 w-[min(680px,calc(100vw-2rem))] rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl shadow-black/40 overflow-hidden z-30">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                      <div>
                        <div className="text-sm font-semibold text-slate-100">
                          Advanced filters
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Combine these with search and pagination.
                        </div>
                      </div>
                      <button
                        onClick={() => setFiltersOpen(false)}
                        className="rounded-xl p-2 text-slate-500 hover:text-slate-100 hover:bg-slate-900"
                        title="Close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-slate-500 mb-1">
                          Category
                        </label>
                        <select
                          value={category || ""}
                          onChange={(e) =>
                            setParams(
                              { category: e.target.value || null },
                              { resetPage: true },
                            )
                          }
                          className="w-full rounded-xl border border-slate-700 bg-slate-950/30 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-cyan-600/80 focus:ring-2 focus:ring-cyan-600/10"
                        >
                          <option value="">All</option>
                          <option value="unclear">Unclear</option>
                          <option value="incorrect">Incorrect</option>
                          <option value="missing_step">Missing Step</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] font-medium text-slate-500 mb-1">
                          Organization
                        </label>
                        <select
                          value={organizationId || ""}
                          onChange={(e) =>
                            setParams(
                              { organizationId: e.target.value || null },
                              { resetPage: true },
                            )
                          }
                          className="w-full rounded-xl border border-slate-700 bg-slate-950/30 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-cyan-600/80 focus:ring-2 focus:ring-cyan-600/10"
                        >
                          <option value="">All</option>
                          {organizations.map((o) => (
                            <option key={o.orgId} value={o.orgId}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] font-medium text-slate-500 mb-1">
                          SOP version
                        </label>
                        <input
                          value={versionNumParam}
                          inputMode="numeric"
                          onChange={(e) =>
                            setParams(
                              { versionNum: e.target.value || null },
                              { resetPage: true },
                            )
                          }
                          placeholder="e.g. 3"
                          className="w-full rounded-xl border border-slate-700 bg-slate-950/30 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-cyan-600/80 focus:ring-2 focus:ring-cyan-600/10"
                        />
                      </div>

                      <div className="sm:col-span-2 lg:col-span-2">
                        <label className="block text-[11px] font-medium text-slate-500 mb-1">
                          Owner (name or email)
                        </label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <input
                            value={ownerInput}
                            onChange={(e) => setOwnerInput(e.target.value)}
                            placeholder="Search owner…"
                            className="w-full rounded-xl border border-slate-700 bg-slate-950/30 pl-9 pr-9 py-2.5 text-sm text-slate-100 outline-none focus:border-cyan-600/80 focus:ring-2 focus:ring-cyan-600/10"
                          />
                          {ownerInput.trim() && (
                            <button
                              onClick={() => {
                                setOwnerInput("");
                                setParams({ owner: null }, { resetPage: true });
                              }}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-500 hover:text-slate-200 hover:bg-slate-800/60"
                              title="Clear owner"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="px-4 py-3 border-t border-slate-800 flex items-center justify-between gap-3">
                      <div className="text-xs text-slate-500">
                        {activeFilterChips.length > 0 ? (
                          <>
                            Active:{" "}
                            <span className="text-slate-200 font-medium">
                              {activeFilterChips.length}
                            </span>
                          </>
                        ) : (
                          "No active advanced filters"
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={clearAll}
                          disabled={activeFilterChips.length === 0}
                          className="rounded-xl border border-slate-700 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Reset
                        </button>
                        <button
                          onClick={() => setFiltersOpen(false)}
                          className="rounded-xl bg-cyan-500/15 border border-cyan-500/30 px-3 py-2 text-sm font-medium text-cyan-200 hover:bg-cyan-500/20"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {activeFilterChips.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {activeFilterChips.slice(0, 6).map((c) => (
                <button
                  key={c.key}
                  onClick={c.onRemove}
                  className="group inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-950/20 px-3 py-1 text-xs text-slate-200 hover:bg-slate-800/60"
                  title="Remove filter"
                >
                  <span className="truncate max-w-[300px]">{c.label}</span>
                  <X className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-200" />
                </button>
              ))}
              {activeFilterChips.length > 6 && (
                <span className="text-xs text-slate-500">
                  +{activeFilterChips.length - 6} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-slate-700/60 bg-slate-900/20 overflow-hidden">
          <div className="overflow-auto">
            <table className="min-w-[980px] w-full text-sm">
              <thead className="bg-slate-900/70 backdrop-blur supports-[backdrop-filter]:bg-slate-900/50 sticky top-0 z-10">
                <tr className="border-b border-slate-700/60">
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    SOP
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Org
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Ver
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Owner
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="bg-slate-950/10">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-700/30">
                      {Array.from({ length: 8 }).map((__, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-3.5 w-full max-w-[220px] rounded bg-slate-800/60 animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-14">
                      <div className="mx-auto max-w-md text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-700 bg-slate-900/40">
                          <Search className="w-5 h-5 text-slate-400" />
                        </div>
                        <div className="mt-4 text-lg font-semibold text-slate-100">
                          No results
                        </div>
                        <div className="mt-1 text-sm text-slate-400">
                          Try adjusting filters, or clear them to see
                          everything.
                        </div>
                        <div className="mt-5 flex justify-center gap-3">
                          <button
                            onClick={clearAll}
                            className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800/60"
                          >
                            Reset filters
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  list.map((f, idx) => (
                    <tr
                      key={f.id}
                      className={`border-b border-slate-700/30 hover:bg-slate-800/35 transition-colors ${
                        idx % 2 === 0 ? "bg-transparent" : "bg-slate-900/10"
                      }`}
                    >
                      <td className="px-5 py-4">
                        <div className="min-w-0">
                          <div
                            className="font-medium text-slate-100 truncate max-w-[280px]"
                            title={f.versionTitle ?? f.sopTitle ?? "Untitled SOP"}
                          >
                            {f.versionTitle ?? f.sopTitle ?? "Untitled SOP"}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            SOP {f.sopId.slice(0, 8)}… · Feedback{" "}
                            {f.id.slice(0, 8)}…
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center rounded-full border border-slate-700 bg-slate-900/40 px-2.5 py-1 text-xs font-medium text-slate-200">
                          {formatFeedbackCategoryLabel(f)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${
                            STATUS_BADGE[f.selectionStale ? "stale" : f.status] ??
                            "border-slate-700 bg-slate-900/40 text-slate-200"
                          }`}
                          title={
                            f.selectionStale
                              ? "Original selected text changed"
                              : undefined
                          }
                        >
                          {f.selectionStale
                            ? "Stale"
                            : f.status === "open"
                              ? "Open"
                              : "Resolved"}
                        </span>
                      </td>
                      <td
                        className="px-5 py-4 text-slate-200 max-w-[220px] truncate"
                        title={f.organization?.name ?? f.orgId ?? ""}
                      >
                        {f.organization?.name ??
                          (f.orgId ? f.orgId.slice(0, 8) + "…" : "—")}
                      </td>
                      <td className="px-5 py-4 text-slate-200 whitespace-nowrap">
                        <span className="rounded-lg border border-slate-700 bg-slate-900/40 px-2 py-1 text-xs">
                          v{f.versionNum ?? "?"}
                        </span>
                      </td>
                      <td
                        className="px-5 py-4 text-slate-200 max-w-[220px] truncate"
                        title={ownerLabel(f)}
                      >
                        {ownerLabel(f)}
                      </td>
                      <td className="px-5 py-4 text-slate-400 whitespace-nowrap">
                        {formatDateTime(f.createdAt)}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div
                          className="relative inline-block"
                          ref={openMenuForId === f.id ? menuRef : undefined}
                        >
                          <button
                            onClick={() =>
                              setOpenMenuForId(
                                openMenuForId === f.id ? null : f.id,
                              )
                            }
                            className="rounded-xl border border-slate-700 bg-slate-950/20 px-2.5 py-2 text-slate-300 hover:bg-slate-800/60 hover:text-white transition-colors"
                            title="Actions"
                            aria-haspopup="menu"
                            aria-expanded={openMenuForId === f.id}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {openMenuForId === f.id && (
                            <div className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-700 bg-slate-950 shadow-2xl shadow-black/40 overflow-hidden z-20">
                              <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                                Actions
                              </div>
                              <button
                                onClick={() => {
                                  setOpenMenuForId(null);
                                  if (f.stepIndex === -1) {
                                    navigate(
                                      `/sop/${f.sopId}?versionId=${encodeURIComponent(f.versionId)}&feedbackId=${encodeURIComponent(f.id)}`,
                                    );
                                  } else {
                                    navigate(
                                      `/sop/${f.sopId}/steps?step=${f.stepIndex}&versionId=${encodeURIComponent(f.versionId)}&feedbackId=${encodeURIComponent(f.id)}`,
                                    );
                                  }
                                }}
                                className="w-full px-3 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-900 flex items-center gap-2"
                              >
                                <ExternalLink className="w-4 h-4 text-slate-400" />
                                Navigate to
                              </button>
                              {canManageFeedback && (
                                <>
                                  <button
                                    onClick={() => {
                                      setOpenMenuForId(null);
                                      handleResolve(f.sopId, f.id, f.status);
                                    }}
                                    className="w-full px-3 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-900 flex items-center gap-2"
                                  >
                                    <CheckCircle className="w-4 h-4 text-slate-400" />
                                    Mark as{" "}
                                    {f.status === "open" ? "resolved" : "open"}
                                  </button>
                                  <div className="h-px bg-slate-800 my-1" />
                                  <button
                                    onClick={() => {
                                      setOpenMenuForId(null);
                                      handleDelete(f.sopId, f.id);
                                    }}
                                    className="w-full px-3 py-2.5 text-left text-sm text-red-300 hover:bg-red-500/10 flex items-center gap-2"
                                  >
                                    <span className="inline-block w-4 h-4" />
                                    Remove…
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination &&
          (pagination.total > 0 || totalPages > 1) && (
            <div className="flex flex-wrap items-center justify-between gap-4 px-2 py-3 border-t border-slate-600/30 mt-4">
              <div className="flex items-center gap-4 flex-wrap">
                <p className="text-slate-400 text-sm">
                  Page {page} of {totalPages}
                  {pagination.total > 0 && (
                    <span className="ml-2">({pagination.total} total)</span>
                  )}
                </p>
                <label className="flex items-center gap-2 text-slate-400 text-sm">
                  <span>Per page:</span>
                  <select
                    value={limit}
                    onChange={(e) =>
                      setParams(
                        {
                          limit: clampPageSize(
                            parseInt(e.target.value, 10) || 20,
                          ),
                          page: 1,
                        },
                        { resetPage: true },
                      )
                    }
                    className="rounded border border-slate-600/50 bg-slate-700/30 text-slate-200 px-2 py-1 text-sm"
                  >
                    {PAGE_SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setParams({ page: Math.max(1, page - 1) })}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  type="button"
                  disabled={!pagination.hasNext}
                  onClick={() => setParams({ page: page + 1 })}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

        <ConfirmDialog
          open={confirmDelete != null}
          onClose={() => {
            if (isDeleting) return;
            setConfirmDelete(null);
          }}
          onConfirm={async () => {
            if (!confirmDelete) return;
            setIsDeleting(true);
            try {
              await deleteFeedback(
                confirmDelete.sopId,
                confirmDelete.feedbackId,
              );
              showToast("Feedback deleted", "", "success");
            } catch {
              showToast("Failed to delete feedback", "", "error");
            } finally {
              setIsDeleting(false);
              setConfirmDelete(null);
            }
          }}
          title="Delete feedback?"
          description="This will permanently delete this feedback."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          isLoading={isDeleting}
        />
      </div>

      <Toast
        open={toast.open}
        onOpenChange={(open) => !open && hideToast()}
        title={toast.title ?? ""}
        description={toast.description}
        type={toast.type}
        duration={toast.duration}
      />
    </div>
  );
};

export default FeedbackPage;
